import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function ThreeHeroScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0, 8.5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    const orbGeometry = new THREE.IcosahedronGeometry(2.25, 1);
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: '#56baf7',
      wireframe: true,
      transparent: true,
      opacity: 0.18,
    });
    const orb = new THREE.Mesh(orbGeometry, wireMaterial);
    group.add(orb);

    const ringGeometry = new THREE.TorusGeometry(3.1, 0.035, 16, 180);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: '#e59f54',
      transparent: true,
      opacity: 0.4,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 1.9;
    ring.rotation.y = Math.PI / 7;
    group.add(ring);

    const pointsGeometry = new THREE.BufferGeometry();
    const pointCount = 140;
    const positions = new Float32Array(pointCount * 3);
    for (let i = 0; i < pointCount; i += 1) {
      const radius = 2.8 + Math.random() * 1.9;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2.2;
    }
    pointsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const pointsMaterial = new THREE.PointsMaterial({
      color: '#f4f2ec',
      size: 0.06,
      transparent: true,
      opacity: 0.85,
    });
    const stars = new THREE.Points(pointsGeometry, pointsMaterial);
    group.add(stars);

    const mouse = { x: 0, y: 0 };
    const handleMouseMove = (event) => {
      mouse.x = event.clientX / window.innerWidth - 0.5;
      mouse.y = event.clientY / window.innerHeight - 0.5;
    };

    const resize = () => {
      const width = mount.clientWidth || window.innerWidth;
      const height = mount.clientHeight || window.innerHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);

    let frameId;
    const tick = () => {
      group.rotation.y += 0.0028;
      group.rotation.x += 0.0014;
      group.rotation.y += mouse.x * 0.0035;
      group.rotation.x -= mouse.y * 0.0025;
      ring.rotation.z += 0.004;
      stars.rotation.y -= 0.0015;
      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(tick);
    };

    tick();

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      mount.removeChild(renderer.domElement);
      orbGeometry.dispose();
      wireMaterial.dispose();
      ringGeometry.dispose();
      ringMaterial.dispose();
      pointsGeometry.dispose();
      pointsMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  return <div className="hero-3d-scene" ref={mountRef} aria-hidden="true" />;
}
